/**
 *
 * Blueprint
 *
 */

import React, { useEffect } from 'react';

import { HStack, Stack, useDisclosure, Box } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDeepCompareEffect } from 'react-use';

import { Card } from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { WithSpinner } from 'components/DataDisplay/Spinner/WithSpinner';
import {
  BackIconButton,
  buttonStyles,
  PrimaryButton,
} from 'components/DataEntry/Button';
import { ConfigurationIcon, TrashIcon } from 'components/Icons';
import { usePageContentContext } from 'components/Layout';
import { DeleteConfirmationModal } from 'components/Overlay/Modal/DeleteConfirmationModal';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import { useBlueprint } from 'containers/PolicyHub/Blueprints/Components/Blueprint/hooks/useBlueprint';
import { Configure } from 'containers/PolicyHub/Blueprints/Components/Utils/Configure';
import { useBlueprintsContext } from 'containers/PolicyHub/Blueprints/hooks/context';
import { selectBlueprints } from 'containers/PolicyHub/Blueprints/selectors';
import { actions } from 'containers/PolicyHub/Blueprints/slice';

import { BlueprintDetails } from './Details/BlueprintDetails';

export const Blueprint = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { contentHeight } = usePageContentContext();
  const { blueprint, blueprintAction } = useSelector(selectBlueprints);
  const deleteDisclosure = useDisclosure();
  const {
    showPolicies,
    setShowPolicies,
    showExceptions,
    setShowExceptions,
  } = useBlueprintsContext();
  const addPolicy = useDisclosure();
  const enforcePolicy = useDisclosure();

  const { getBlueprint } = useBlueprint();

  useDeepCompareEffect(() => {
    getBlueprint();
  }, [getBlueprint]);

  useEffect(() => {
    return () => {
      dispatch(actions.resetBlueprintForm());
    };
  }, [dispatch, addPolicy, setShowPolicies, setShowExceptions]);

  const handleDelete = () => {
    dispatch(
      actions.deleteBlueprint({
        q: { blueprintId: blueprint.data?.id },
        onSuccess: () => {
          deleteDisclosure.onClose();
          navigate('/policy-hub');
        },
      }),
    );
  };

  return (
    <Stack h={contentHeight - 6 + 'px'} w={'full'} spacing={5}>
      <Card styles={{ card: { w: 'full', h: 'full', py: 3, px: 6 } }}>
        <Stack w="full" h="full" spacing={2}>
          <HStack justify="space-between" w="full" h={16}>
            <HStack color="primary">
              <BackIconButton
                onClick={() =>
                  addPolicy.isOpen
                    ? addPolicy.onClose?.()
                    : showPolicies
                    ? setShowPolicies(false)
                    : showExceptions
                    ? setShowExceptions(false)
                    : navigate('/policy-hub/blueprint')
                }
              />
              <Box
                onClick={() => {
                  setShowExceptions(false);
                  setShowPolicies(false);
                  addPolicy.onClose?.();
                  navigate('/policy-hub/blueprint');
                }}
                cursor="pointer"
              >
                Policy Blueprint
              </Box>
              {(showPolicies || showExceptions) && !blueprint.isLoading && (
                <Box>/</Box>
              )}
              <Box
                onClick={() => {
                  setShowPolicies(false);
                  setShowExceptions(false);
                  addPolicy.onClose();
                }}
                cursor="pointer"
              >
                {(showPolicies || showExceptions) && !blueprint.isLoading
                  ? blueprint.data.name
                  : ''}
              </Box>
              {addPolicy.isOpen && !blueprint.isLoading && <Box>/</Box>}
              <Box onClick={() => addPolicy.onClose?.()} cursor="pointer">
                {addPolicy.isOpen && !blueprint.isLoading ? 'Policies' : ''}
              </Box>
            </HStack>

            {!(showPolicies || showExceptions) && (
              <HStack>
                <PrimaryButton
                  aria-label="configure"
                  onClick={() => enforcePolicy.onOpen()}
                  tooltip="Apply policies to environments"
                  leftIcon={<ConfigurationIcon p={0.5} />}
                  tooltipStyles={{ tooltip: { w: 32, textAlign: 'center' } }}
                  color="white"
                  bg="primary"
                >
                  Enforce
                </PrimaryButton>
                <PrimaryButton
                  aria-label="delete"
                  onClick={() => deleteDisclosure.onOpen()}
                  isDisabled={blueprint.data?.read_only || blueprint.isLoading}
                  tooltip={
                    blueprint.data?.read_only || blueprint.isLoading
                      ? 'Deletion not allowed'
                      : 'Delete blueprint'
                  }
                  tooltipStyles={{ tooltip: { w: 32, textAlign: 'center' } }}
                  leftIcon={<TrashIcon />}
                  {...buttonStyles('delete')}
                >
                  Delete
                </PrimaryButton>
                {deleteDisclosure.isOpen && (
                  <DeleteConfirmationModal
                    {...deleteDisclosure}
                    onConfirm={handleDelete}
                    name={blueprint.data?.name}
                    type={'blueprint'}
                    isLoading={blueprintAction.isLoading}
                  />
                )}
                {enforcePolicy.isOpen && (
                  <Configure
                    isOpen={enforcePolicy.isOpen}
                    onClose={enforcePolicy.onClose}
                    onSuccess={() => {
                      enforcePolicy.onClose();
                      getBlueprint();
                    }}
                  />
                )}
              </HStack>
            )}
          </HStack>

          <WithSpinner
            loadStatus={{ loading: blueprint.isLoading }}
            spinnerSize="xl"
          >
            <ActiveResourceProvider>
              <BlueprintDetails addPolicy={addPolicy} />
            </ActiveResourceProvider>
          </WithSpinner>
        </Stack>
      </Card>
      <LearnMore />
    </Stack>
  );
};
